/**
 * Business Structure Helper Functions
 *
 * These utilities help determine business structure type based on member's usertype field.
 * The usertype follows the pattern: {source}-{destination} where destination indicates
 * the final business structure type.
 */

type BusinessStructureType = 'LLC' | 'SCorp' | 'SP' | 'Unknown';

/**
 * Determines the business structure type from a usertype string
 * @param usertype - The member's usertype (e.g., "SP-SC", "LLC-SC", "SC-SC")
 * @returns The business structure type
 */
export function getBusinessStructureType(usertype: string): BusinessStructureType {
    if (!usertype || typeof usertype !== 'string') {
        return 'Unknown';
    }

    const destination = usertype.split('-')[1];

    switch (destination) {
        case 'LLC':
            return 'LLC';
        case 'SC':
            return 'SCorp'; // S-Corporation
        case 'SP':
            return 'SP'; // Sole Proprietor
        default:
            return 'Unknown';
    }
}

/**
 * Checks if a member is an S-Corporation based on their usertype
 * @param usertype - The member's usertype
 * @returns true if the member is an S-Corp, false otherwise
 */
export function isSCorpMember(usertype: string): boolean {
    return getBusinessStructureType(usertype) === 'SCorp';
}

/**
 * Checks if a member is an LLC based on their usertype
 * @param usertype - The member's usertype
 * @returns true if the member is an LLC, false otherwise
 */
export function isLLCMember(usertype: string): boolean {
    return getBusinessStructureType(usertype) === 'LLC';
}

/**
 * Checks if a member is going through entity formation based on their usertype.
 * Entity formation members have source 'SP' (sole proprietor forming a new entity).
 * @param usertype - The member's usertype (e.g., "SP-SC", "SP-LLC")
 * @returns true if the member is in entity formation, false otherwise
 */
export function isEntityFormation(usertype: string): boolean {
    return usertype?.split('-')[0] === 'SP';
}
